import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faRotateRight, faRoute, faMapLocationDot } from "@fortawesome/free-solid-svg-icons"
import { DeliveryEntity } from "../../Objects/DeliveryEntity/DeliveryEntity"
import { DeliveryRoutePlan } from "../../Objects/DeliveryEntity/DeliveryRoutePlan"
import { useDeliveryRoutePlan } from "../../hooks/Delivery/useDeliveryRoutePlan"
import { countRouteRelevantDeliveries, openMultiStopGoogleMaps } from "../../hooks/Delivery/deliveryListUtils"
import { CourierRoutePlanBody } from "./Components/CourierRoutePlanBody"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"

interface Props {
  deliveries: DeliveryEntity[]
  onClose: () => void
}

export function CourierRoutePlanPage({ deliveries, onClose }: Props) {
  const { t } = useTranslation("frontend")

  const { isLoadingRoutePlan, handleGetRoutePlan } = useDeliveryRoutePlan()

  const [plan, setPlan] = useState<DeliveryRoutePlan | null>(null)

  const [selectedOptionKey, setSelectedOptionKey] = useState<string>()

  const [expandedStopKey, setExpandedStopKey] = useState<string>()

  const routeRelevantCount = useMemo(() => countRouteRelevantDeliveries(deliveries), [deliveries])

  const fetchPlan = () => {
    handleGetRoutePlan((nextPlan) => {
      setPlan(nextPlan)

      setSelectedOptionKey((current) => {
        if (current && nextPlan.options.some((option) => option.key === current)) {
          return current
        }

        return nextPlan.recommendedOptionKey ?? nextPlan.options[0]?.key
      })
    })
  }

  useEffect(() => {
    fetchPlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeRelevantCount])

  const selectedOption = plan?.options.find((option) => option.key === selectedOptionKey) ?? plan?.options[0]

  return (
    <Animate type={AnimationTypes.slideUpIn} duration="0.25s">
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-50">
        <header className="shrink-0 bg-gradient-to-r from-orange-500 to-red-500 px-3 pb-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] shadow-md">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition active:scale-95"
              data-interaction-description-key="interactionDescriptions:pages.delivery.courierRoutePlanPage.button.onClose"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
            </button>

            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-bold uppercase tracking-wide text-orange-100">
                {t("route_plan.title")}
              </p>

              <h1 className="truncate text-lg font-black text-white">
                {t("route_plan.deliveries_count", { count: plan?.deliveriesCount ?? routeRelevantCount })}
              </h1>
            </div>

            <button
              type="button"
              onClick={fetchPlan}
              disabled={isLoadingRoutePlan}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition active:scale-95 disabled:opacity-60"
              data-interaction-description-key="interactionDescriptions:pages.delivery.courierRoutePlanPage.button.fetchPlan"
            >
              <FontAwesomeIcon icon={faRotateRight} spin={isLoadingRoutePlan} className="text-base" />
            </button>
          </div>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
          {isLoadingRoutePlan && !plan && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
              <FontAwesomeIcon icon={faRotateRight} spin className="text-2xl" />
              <p className="text-sm font-semibold">{t("route_plan.loading")}</p>
            </div>
          )}

          {!isLoadingRoutePlan && (!plan || plan.options.length === 0) && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-slate-400">
              <FontAwesomeIcon icon={faRoute} className="text-2xl" />
              <p className="text-sm font-semibold">{t("route_plan.empty")}</p>
            </div>
          )}

          {plan && plan.options.length > 0 && (
            <CourierRoutePlanBody
              plan={plan}
              selectedOptionKey={selectedOptionKey}
              onSelectOption={(key) => {
                setSelectedOptionKey(key)
                setExpandedStopKey(undefined)
              }}
              expandedStopKey={expandedStopKey}
              onToggleStop={(key) => setExpandedStopKey((current) => (current === key ? undefined : key))}
            />
          )}
        </div>

        {plan && selectedOption && selectedOption.stops.length > 0 && (
          <div className="shrink-0 border-t border-slate-200 bg-white px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]">
            <button
              type="button"
              onClick={() => openMultiStopGoogleMaps(selectedOption)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 py-3.5 text-sm font-black text-white shadow-md transition-transform active:scale-[0.98]"
              data-interaction-description-key="interactionDescriptions:pages.delivery.courierRoutePlanPage.button.openGoogleMaps"
            >
              <FontAwesomeIcon icon={faMapLocationDot} />

              {t("route_plan.open_in_maps")}
            </button>
          </div>
        )}
      </div>
    </Animate>
  )
}
